import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import I18nField from './I18nField';
import ThumbnailUpload from './ThumbnailUpload';
import { createI18nObject, getDefaultLang } from '../utils/i18n';

export default function MaterialForm({ material, onSave, onCancel }) {
  const { user } = useAuth();
  const supportedLanguages = user?.supportedLanguages || ['pt'];

  const [formData, setFormData] = useState({
    name: '',
    key: '',
    filamentType: '',
    thumbnail: null,
    description: createI18nObject(supportedLanguages),
    applications: [createI18nObject(supportedLanguages)],
    pros: [createI18nObject(supportedLanguages)],
    cons: [createI18nObject(supportedLanguages)],
    internalNotes: '',
    pricePerPrintedKg: 1,
    showInSite: true,
    displayOrder: 0
  });
  const [filamentTypeOverridden, setFilamentTypeOverridden] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestingPrice, setSuggestingPrice] = useState(false);

  useEffect(() => {
    if (material) {
      setFilamentTypeOverridden(true);
      setFormData({
        name: material.name || '',
        key: material.key || '',
        filamentType: material.filamentType || '',
        thumbnail: material.thumbnail || null,
        description: material.description || createI18nObject(supportedLanguages),
        applications: material.applications && material.applications.length > 0
          ? material.applications
          : [createI18nObject(supportedLanguages)],
        pros: material.pros && material.pros.length > 0
          ? material.pros
          : [createI18nObject(supportedLanguages)],
        cons: material.cons && material.cons.length > 0
          ? material.cons
          : [createI18nObject(supportedLanguages)],
        internalNotes: material.internalNotes || '',
        pricePerPrintedKg: material.pricePerPrintedKg || 1,
        showInSite: material.showInSite ?? true,
        displayOrder: material.displayOrder || 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init form when prop changes
  }, [material]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const defaultLang = getDefaultLang(supportedLanguages);
      const { thumbnail: _Thumbnail, ...rest } = formData;
      const dataToSave = {
        ...rest,
        filamentType: formData.filamentType.toUpperCase(),
        applications: formData.applications.filter(item => (item[defaultLang] || '').trim()),
        pros: formData.pros.filter(item => (item[defaultLang] || '').trim()),
        cons: formData.cons.filter(item => (item[defaultLang] || '').trim())
      };

      if (thumbnailFile) {
        const fd = new FormData();
        fd.append('thumbnail', thumbnailFile);
        // Append all other fields as JSON
        Object.entries(dataToSave).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            fd.append(key, JSON.stringify(value));
          } else {
            fd.append(key, value);
          }
        });
        await onSave(fd);
      } else {
        await onSave(dataToSave);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save material');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    if (name === 'filamentType') {
      setFilamentTypeOverridden(true);
      setFormData({ ...formData, filamentType: value.toUpperCase() });
      return;
    }

    if (name === 'key' && !material && !filamentTypeOverridden) {
      setFormData({ ...formData, key: value, filamentType: value.toUpperCase() });
      return;
    }

    setFormData({ ...formData, [name]: newValue });
  };

  const handleDescriptionChange = (lang, value) => {
    setFormData({
      ...formData,
      description: { ...formData.description, [lang]: value }
    });
  };

  const handleArrayChange = (arrayName, index, lang, value) => {
    const newArray = [...formData[arrayName]];
    newArray[index] = { ...newArray[index], [lang]: value };
    setFormData({ ...formData, [arrayName]: newArray });
  };

  const addArrayItem = (arrayName) => {
    setFormData({
      ...formData,
      [arrayName]: [...formData[arrayName], createI18nObject(supportedLanguages)]
    });
  };

  const removeArrayItem = (arrayName, index) => {
    const newArray = formData[arrayName].filter((_, i) => i !== index);
    setFormData({ ...formData, [arrayName]: newArray });
  };

  const handleSuggestPrice = async () => {
    setSuggestingPrice(true);
    try {
      // Dummy promise simulating an API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Simulate a suggested price calculation (random between 1.5 and 3.5)
      const suggestedPrice = (Math.random() * 2 + 1.5).toFixed(2);

      setFormData({
        ...formData,
        pricePerPrintedKg: parseFloat(suggestedPrice)
      });
    } catch {
      setError('Failed to suggest price');
    } finally {
      setSuggestingPrice(false);
    }
  };

  const renderArraySection = (arrayName, label) => (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">{label} *</label>
      {formData[arrayName].map((item, index) => (
        <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <I18nField
            value={item}
            onChange={(lang, value) => handleArrayChange(arrayName, index, lang, value)}
            languages={supportedLanguages}
            required
            disabled={loading}
            maxLength={1000}
          />
          {formData[arrayName].length > 1 && (
            <button
              type="button"
              onClick={() => removeArrayItem(arrayName, index)}
              disabled={loading}
              className="mt-3 inline-flex items-center px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              Remove
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => addArrayItem(arrayName)}
        disabled={loading}
        className="inline-flex items-center px-4 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors font-medium"
      >
        <PlusIcon className="h-4 w-4 mr-1" />
        Add {label}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {material ? 'Edit Material' : 'Add Material'}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={50}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="key" className="block text-sm font-medium text-slate-700 mb-2">
                  Key *
                </label>
                <input
                  type="text"
                  id="key"
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={20}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            {/* Filament Type */}
            <div>
              <label htmlFor="filamentType" className="block text-sm font-medium text-slate-700 mb-2">
                Filament Type *
              </label>
              <input
                type="text"
                id="filamentType"
                name="filamentType"
                value={formData.filamentType}
                onChange={handleChange}
                required
                disabled={loading}
                maxLength={50}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 uppercase"
              />
              <p className="mt-1 text-xs text-slate-500">
                {!material ? 'Auto-filled from Key. Override if needed.' : 'Identifier used for slicer filament type matching'}
              </p>
            </div>

            {/* Thumbnail */}
            <ThumbnailUpload
              currentUrl={formData.thumbnail?.url}
              file={thumbnailFile}
              onChange={setThumbnailFile}
              disabled={loading}
            />

            {/* Description */}
            <I18nField
              label="Description"
              value={formData.description}
              onChange={(lang, value) => handleDescriptionChange(lang, value)}
              languages={supportedLanguages}
              type="textarea"
              required
              disabled={loading}
              rows={3}
              maxLength={1000}
            />

            {/* Arrays */}
            {renderArraySection('applications', 'Applications')}
            {renderArraySection('pros', 'Pros')}
            {renderArraySection('cons', 'Cons')}

            {/* Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="displayOrder" className="block text-sm font-medium text-slate-700 mb-2">
                  Display Order *
                </label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min={0}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="showInSite"
                    checked={formData.showInSite}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Show in Site</span>
                </label>
              </div>
            </div>

            {/* Internal Notes */}
            <div>
              <label htmlFor="internalNotes" className="block text-sm font-medium text-slate-700 mb-2">
                Internal Notes
              </label>
              <textarea
                id="internalNotes"
                name="internalNotes"
                value={formData.internalNotes}
                onChange={handleChange}
                disabled={loading}
                rows={3}
                maxLength={1000}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 resize-none"
              />
            </div>

            {/* Pricing */}
            <div>
              <label htmlFor="pricePerPrintedKg" className="block text-sm font-medium text-slate-700 mb-2">
                Price per Printed Kg (€) *
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  id="pricePerPrintedKg"
                  name="pricePerPrintedKg"
                  value={formData.pricePerPrintedKg}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min={0.01}
                  step={0.01}
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
                {material && (
                  <button
                    type="button"
                    onClick={handleSuggestPrice}
                    disabled={loading || suggestingPrice}
                    className="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
                    title="Suggest Price"
                  >
                    <LightBulbIcon className="h-5 w-5 mr-1" />
                    {suggestingPrice ? 'Suggesting...' : 'Suggest Price'}
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Pricing multiplier applied to the base filament cost for final customer pricing
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
